import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/** Canon `.pill`: 11.5px, px-2 py-px, 999px. One status/tag shape. tone = colour role, not a one-off colour. */
export const pillVariants = cva(
  "inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-px text-pill",
  {
    variants: {
      tone: {
        accent: "bg-hl/13 text-hl",
        ok: "bg-ok/13 text-ok",
        plain: "border border-line text-muted-foreground",
        fact: "bg-warn/12 text-warn",
        draft: "border border-dashed border-draft/60 text-draft",
        guess: "border border-dashed border-ai/55 text-ai",
        reserve: "bg-reserve/12 text-reserve",
        own: "bg-own/13 text-own",
        danger: "bg-danger/14 text-danger",
      },
    },
    defaultVariants: { tone: "plain" },
  },
);

export interface PillProps extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof pillVariants> {}

export function Pill({ tone, className, ...props }: PillProps) {
  return <span className={cn(pillVariants({ tone }), className)} {...props} />;
}

/** Canon `.chip` / `.tok`: a pickable pill. aria-pressed marks the picked one. */
export const Chip = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ className, type = "button", ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(
        "rounded-full border border-line px-2.5 py-0.5 text-left text-meta text-muted-foreground transition-colors hover:border-ai hover:text-fg aria-pressed:border-ai aria-pressed:text-ai disabled:opacity-50",
        className,
      )}
      {...props}
    />
  ),
);
Chip.displayName = "Chip";
